import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useTheme } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Target, Shield, TrendingUp, TrendingDown,
  CheckCircle2, XCircle, AlertTriangle, Sparkles,
  RefreshCw, Check, X, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import { toast } from "sonner";

export default function CloseTradeModal({ isOpen, alert, livePrice, onClose, onSuccess }) {
  const { theme } = useTheme();
  const isLight = theme === "light";

  const [reason, setReason] = useState("TARGET HIT");
  const [exitPrice, setExitPrice] = useState("");
  const [remark, setRemark] = useState("");
  const [loading, setLoading] = useState(false);

  const cmp = typeof livePrice === "object" ? livePrice?.last_price : livePrice;

  // Initialize or reset when modal opens
  useEffect(() => {
    if (!isOpen || !alert) return;

    // Default reason & exit price
    const defaultReason = "TARGET HIT";
    setReason(defaultReason);

    let defaultPrice = "";
    if (alert.target != null) {
      defaultPrice = String(alert.target);
    } else if (cmp != null && cmp > 0) {
      defaultPrice = String(cmp);
    } else if (alert.entry != null) {
      defaultPrice = String(alert.entry);
    }
    setExitPrice(defaultPrice);

    setRemark(alert.admin_remark ? `Target reached: ${alert.admin_remark}` : "Target achieved cleanly. Trade closed.");
  }, [isOpen, alert, cmp]);

  // Handle reason chip change with auto price update
  const handleSelectReason = (newReason) => {
    setReason(newReason);
    if (!alert) return;

    if (newReason === "TARGET HIT" && alert.target != null) {
      setExitPrice(String(alert.target));
      setRemark("Target achieved cleanly. Full exit booked.");
    } else if (newReason === "SL HIT" && alert.stop_loss != null) {
      setExitPrice(String(alert.stop_loss));
      setRemark("Stop Loss hit. Risk protected as per setup.");
    } else if (newReason === "PROFIT BOOKED") {
      setExitPrice(cmp != null && cmp > 0 ? String(cmp) : String(alert.target || alert.entry || ""));
      setRemark("Manual profit booking executed. Capital secured.");
    } else if (newReason === "MANUAL EXIT") {
      setExitPrice(cmp != null && cmp > 0 ? String(cmp) : String(alert.entry || ""));
      setRemark("Trade invalidated by structure change. Early exit.");
    }
  };

  // P&L calculation
  const pnl = useMemo(() => {
    if (!alert || !exitPrice) return null;
    const entry = parseFloat(alert.entry);
    const exit = parseFloat(exitPrice);
    if (isNaN(entry) || isNaN(exit) || entry <= 0 || exit <= 0) return null;

    const isDemand = (alert.zone_type || "DEMAND").toUpperCase() === "DEMAND";
    const diff = isDemand ? exit - entry : entry - exit;
    const pct = (diff / entry) * 100;
    return {
      pts: diff.toFixed(2),
      pct: pct.toFixed(2),
      isProfit: diff >= 0
    };
  }, [alert, exitPrice]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!alert) return;

    const alertId = alert.alert_id || alert.id;
    if (!alertId) {
      toast.error("Alert ID missing");
      return;
    }

    const priceVal = exitPrice ? parseFloat(exitPrice) : null;
    if (priceVal !== null && (isNaN(priceVal) || priceVal <= 0)) {
      toast.error("Please enter a valid exit price");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        reason: reason.trim(),
        exit_price: priceVal,
        remark: remark.trim()
      };

      const res = await axios.put(`${API}/drishti/alerts/${alertId}/close`, payload, {
        withCredentials: true
      });

      toast.success(`Trade for ${alert.script_name} closed successfully!`, {
        description: `Reason: ${reason} | Exit: ₹${priceVal || "CMP"}`
      });

      const isTargetOutcome = reason.toUpperCase().includes("TARGET") || reason.toUpperCase().includes("PROFIT");
      onSuccess?.(res.data?.alert || {
        ...alert,
        is_closed: true,
        close_reason: reason,
        close_price: priceVal,
        target_hit: isTargetOutcome,
        sl_hit: !isTargetOutcome && (reason.toUpperCase().includes("SL") || reason.toUpperCase().includes("STOP"))
      });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to close trade");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !alert) return null;

  const isDemand = (alert.zone_type || "DEMAND").toUpperCase() === "DEMAND";

  const REASONS = [
    {
      id: "TARGET HIT",
      label: "🎯 Target Hit",
      lightActive: "bg-emerald-50 border-emerald-500 text-emerald-800 ring-2 ring-emerald-500/20 shadow-sm font-bold",
      darkActive: "bg-[#00FF88]/20 border-[#00FF88] text-[#00FF88] ring-2 ring-[#00FF88]/30 shadow-[0_0_12px_rgba(0,255,136,0.25)] font-bold",
      lightInactive: "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-medium",
      darkInactive: "bg-white/[0.04] border-white/10 text-zinc-300 hover:bg-white/[0.08] hover:text-white font-medium"
    },
    {
      id: "SL HIT",
      label: "⛔ SL Hit",
      lightActive: "bg-rose-50 border-rose-500 text-rose-800 ring-2 ring-rose-500/20 shadow-sm font-bold",
      darkActive: "bg-[#FF3B30]/20 border-[#FF3B30] text-[#FF3B30] ring-2 ring-[#FF3B30]/30 shadow-[0_0_12px_rgba(255,59,48,0.25)] font-bold",
      lightInactive: "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-medium",
      darkInactive: "bg-white/[0.04] border-white/10 text-zinc-300 hover:bg-white/[0.08] hover:text-white font-medium"
    },
    {
      id: "PROFIT BOOKED",
      label: "💰 Profit Booked",
      lightActive: "bg-amber-50 border-amber-500 text-amber-900 ring-2 ring-amber-500/20 shadow-sm font-bold",
      darkActive: "bg-amber-400/20 border-amber-400 text-amber-300 ring-2 ring-amber-400/30 shadow-[0_0_12px_rgba(251,191,36,0.25)] font-bold",
      lightInactive: "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-medium",
      darkInactive: "bg-white/[0.04] border-white/10 text-zinc-300 hover:bg-white/[0.08] hover:text-white font-medium"
    },
    {
      id: "MANUAL EXIT",
      label: "❌ Manual Exit",
      lightActive: "bg-purple-50 border-purple-500 text-purple-900 ring-2 ring-purple-500/20 shadow-sm font-bold",
      darkActive: "bg-purple-500/25 border-purple-400 text-purple-300 ring-2 ring-purple-400/30 shadow-[0_0_12px_rgba(168,85,247,0.25)] font-bold",
      lightInactive: "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-medium",
      darkInactive: "bg-white/[0.04] border-white/10 text-zinc-300 hover:bg-white/[0.08] hover:text-white font-medium"
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`max-w-lg p-5 sm:p-6 rounded-2xl shadow-2xl transition-all border ${
        isLight
          ? "bg-white border-slate-200 text-slate-900 shadow-slate-300/40"
          : "bg-[#0C0C12] border-white/10 text-white shadow-black/80"
      }`}>
        <DialogHeader className={`space-y-1.5 pb-3 border-b ${
          isLight ? "border-slate-100" : "border-white/10"
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold ${
                isLight
                  ? "bg-purple-50 text-purple-600 border border-purple-200"
                  : "bg-purple-500/15 border border-purple-500/30 text-purple-400"
              }`}>
                <Target className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className={`text-lg font-bold font-outfit flex items-center gap-2 ${
                  isLight ? "text-slate-900" : "text-white"
                }`}>
                  <span>Close Trade: {alert.script_name}</span>
                  <Badge className={`border text-[10px] font-mono font-bold uppercase ${
                    isDemand
                      ? (isLight ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-[#00FF88]/20 text-[#00FF88] border-[#00FF88]/30")
                      : (isLight ? "bg-rose-50 text-rose-700 border-rose-300" : "bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/30")
                  }`}>
                    {alert.zone_type}
                  </Badge>
                </DialogTitle>
                <p className={`text-xs font-plex ${isLight ? "text-slate-500" : "text-gray-400"}`}>
                  Log trade outcome, lock in exit price, and update status across all platforms
                </p>
              </div>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {/* Trade Snapshot Banner */}
          <div className={`p-3 rounded-xl border grid grid-cols-4 gap-2 text-center ${
            isLight ? "bg-slate-50 border-slate-200" : "bg-white/[0.02] border-white/[0.06]"
          }`}>
            <div>
              <p className={`text-[10px] font-mono uppercase ${isLight ? "text-slate-500" : "text-gray-400"}`}>Entry</p>
              <p className={`text-sm font-bold font-mono ${isLight ? "text-slate-900" : "text-white"}`}>₹{alert.entry ?? "—"}</p>
            </div>
            <div>
              <p className="text-[10px] text-rose-600 dark:text-[#FF3B30] font-mono uppercase font-semibold">SL</p>
              <p className="text-sm font-bold font-mono text-rose-600 dark:text-[#FF3B30]">₹{alert.stop_loss ?? "—"}</p>
            </div>
            <div>
              <p className="text-[10px] text-emerald-600 dark:text-[#00FF88] font-mono uppercase font-semibold">Target</p>
              <p className="text-sm font-bold font-mono text-emerald-600 dark:text-[#00FF88]">₹{alert.target ?? "—"}</p>
            </div>
            <div>
              <p className="text-[10px] text-sky-600 dark:text-[#00F0FF] font-mono uppercase font-semibold">CMP</p>
              <p className="text-sm font-bold font-mono text-sky-600 dark:text-[#00F0FF]">₹{cmp || alert.entry || "—"}</p>
            </div>
          </div>

          {/* Quick Reason Chips */}
          <div className="space-y-1.5">
            <Label className={`text-xs font-mono uppercase flex items-center gap-1.5 ${
              isLight ? "text-slate-700 font-semibold" : "text-gray-400"
            }`}>
              <span>Select Outcome Reason:</span>
            </Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {REASONS.map((chip) => {
                const active = reason === chip.id;
                const activeClasses = isLight ? chip.lightActive : chip.darkActive;
                const inactiveClasses = isLight ? chip.lightInactive : chip.darkInactive;
                return (
                  <button
                    key={chip.id}
                    type="button"
                    onClick={() => handleSelectReason(chip.id)}
                    className={`py-2 px-2.5 rounded-xl text-xs font-outfit border transition-all text-center flex items-center justify-center gap-1 cursor-pointer ${
                      active ? activeClasses : inactiveClasses
                    }`}
                  >
                    <span>{chip.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Exit Price Input & Live P&L Preview */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className={`text-xs font-mono uppercase ${
                isLight ? "text-slate-700 font-semibold" : "text-gray-400"
              }`}>
                Exit Price (₹)
              </Label>
              {pnl && (
                <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded border ${
                  pnl.isProfit
                    ? (isLight ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/30")
                    : (isLight ? "bg-rose-50 text-rose-700 border-rose-300" : "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30")
                }`}>
                  {pnl.isProfit ? "+" : ""}{pnl.pts} pts ({pnl.isProfit ? "+" : ""}{pnl.pct}%)
                </span>
              )}
            </div>
            <div className="relative">
              <span className={`absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm ${
                isLight ? "text-slate-500 font-bold" : "text-gray-500"
              }`}>₹</span>
              <Input
                type="number"
                step="0.05"
                value={exitPrice}
                onChange={(e) => setExitPrice(e.target.value)}
                placeholder="0.00"
                className={`pl-8 font-mono font-bold text-base h-10 transition-all ${
                  isLight
                    ? "bg-white border-slate-300 text-slate-900 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                    : "bg-[#14141E] border-white/15 text-white focus:border-purple-400"
                }`}
                required
              />
            </div>
          </div>

          {/* Close Remark */}
          <div className="space-y-1.5">
            <Label className={`text-xs font-mono uppercase ${
              isLight ? "text-slate-700 font-semibold" : "text-gray-400"
            }`}>
              Admin Remark / Closing Note
            </Label>
            <Input
              type="text"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="e.g. Target reached cleanly. Trail SL booked full profit."
              className={`text-xs h-9 transition-all ${
                isLight
                  ? "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                  : "bg-[#14141E] border-white/15 text-white placeholder:text-zinc-500 focus:border-purple-400"
              }`}
            />
            {/* Quick remark suggestions */}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {[
                "🎯 Target hit 100% cleanly",
                "⚡ Strict SL trail exit",
                "💰 Profit booked at resistance",
                "🛡️ Safe breakeven exit"
              ].map((sug) => (
                <button
                  key={sug}
                  type="button"
                  onClick={() => setRemark(sug)}
                  className={`text-[11px] font-mono px-2.5 py-1 rounded-lg border transition-all cursor-pointer ${
                    isLight
                      ? "bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border-slate-200 font-medium"
                      : "bg-white/[0.05] hover:bg-white/[0.1] text-zinc-300 hover:text-white border-white/10"
                  }`}
                >
                  {sug}
                </button>
              ))}
            </div>
          </div>

          {/* Action CTAs */}
          <div className={`flex items-center justify-end gap-2 pt-3 border-t ${
            isLight ? "border-slate-100" : "border-white/10"
          }`}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={loading}
              className={`h-9 px-4 font-semibold ${
                isLight ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100" : "text-gray-400 hover:text-white"
              }`}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold font-outfit text-xs h-9 px-5 rounded-xl shadow-lg shadow-purple-600/25 flex items-center gap-1.5"
            >
              {loading ? (
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4 stroke-[2.5]" />
                  Confirm &amp; Close Trade
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
